import { useState } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  downloadComplianceReport,
  getComplianceReport,
  type ComplianceReportResponse,
} from '../api/complianceReports.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatPeriod } from '../lib/format.js';

/**
 * /compliance-reports/:id — read-only metadata view for one report.
 * No editing — reports are immutable filing artefacts.
 */
export const complianceReportDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/compliance-reports/$id',
  component: ComplianceReportDetailPage,
});

function ComplianceReportDetailPage(): JSX.Element {
  const { id } = complianceReportDetailRoute.useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['compliance-reports', 'detail', id],
    queryFn: () => getComplianceReport(id),
  });

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <nav className="text-xs text-gray-500">
        <Link to="/compliance-reports" className="hover:underline">
          Compliance reports
        </Link>{' '}
        / {data ? formatPeriod(data.periodYear, data.periodMonth) : id}
      </nav>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState error={error} /> : null}
      {data ? <ReportDetail report={data} /> : null}
    </section>
  );
}

function ReportDetail({ report }: { report: ComplianceReportResponse }): JSX.Element {
  return (
    <>
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">
          Compliance report — {formatPeriod(report.periodYear, report.periodMonth)}
        </h1>
      </header>

      <div className="rounded border border-gray-900 p-4 space-y-2">
        <h3 className="text-xs uppercase tracking-wide text-gray-400">Metadata</h3>
        <KV k="Period" v={formatPeriod(report.periodYear, report.periodMonth)} />
        <KV k="Country" v={report.countryCode} />
        <KV k="Regulator format" v={report.regulatorFormat} mono />
        <div className="flex items-start justify-between gap-3 text-sm">
          <span className="text-gray-500">Payroll run</span>
          <Link
            to="/payroll-runs/$id"
            params={{ id: report.payrollRunId }}
            className="font-mono text-xs text-gray-300 hover:underline break-all text-right"
          >
            {report.payrollRunId}
          </Link>
        </div>
        <KV k="Generated" v={report.generatedAt.slice(0, 19).replace('T', ' ')} />
        <KV k="Generated by" v={report.generatedById} mono />
        <KV k="Row count" v={`${report.rowCount} employees`} />
        <KV k="Column count" v={String(report.columnCount)} />
        <ShaRow sha={report.csvSha256} />
      </div>

      <DownloadButton id={report.id} />
    </>
  );
}

function ShaRow({ sha }: { sha: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-gray-500">SHA-256</span>
      <span className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-xs text-gray-300 break-all text-right" title={sha}>
          {sha}
        </span>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(sha);
            setCopied(true);
            setTimeout(() => setCopied(false), 1_500);
          }}
          className="shrink-0 text-xs border border-gray-700 rounded px-2 py-0.5 text-gray-400 hover:bg-gray-900"
          aria-label="Copy SHA-256"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </span>
    </div>
  );
}

/** Bottom download button — full-width, primary affordance. */
function DownloadButton({ id }: { id: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setErr(null);
          setBusy(true);
          try {
            await downloadComplianceReport(id);
          } catch (caught) {
            setErr(caught instanceof ApiError ? caught.message : 'Download failed. Try again.');
          } finally {
            setBusy(false);
          }
        }}
        className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
        aria-label={`Download CSV for report ${id}`}
      >
        {busy ? 'Downloading…' : 'Download CSV'}
      </button>
      {err ? <Alert variant="error">{err}</Alert> : null}
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-gray-500">{k}</span>
      <span className={`${mono ? 'font-mono text-xs break-all text-right' : 'text-gray-300'}`}>
        {v}
      </span>
    </div>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">Loading...</div>
  );
}

function ErrorState({ error }: { error: unknown }): JSX.Element {
  const is404 = error instanceof ApiError && error.status === 404;
  return (
    <Alert
      variant="error"
      title={is404 ? 'Compliance report not found' : 'Could not load compliance report'}
      action={
        <Link
          to="/compliance-reports"
          className="inline-block rounded border border-red-800 px-3 py-1 text-xs hover:bg-red-900/40"
        >
          Back to compliance reports
        </Link>
      }
    >
      {error instanceof Error ? error.message : 'Unknown error'}
    </Alert>
  );
}
